import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// redux slice
import { selectUserDetails } from "../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";

// mui components
import {
	Box,
	CssBaseline,
	AppBar,
	Stack,
	Toolbar,
	IconButton,
} from "@mui/material";

// mui icons
import MenuIcon from "@mui/icons-material/Menu";
import { ArrowForwardIosOutlined } from "@mui/icons-material";

// custom components
import CustomDrawer from "../custom-drawer";
import OrganizationDropdown from "../organization-dropdown";
import Heading3 from "../typography/heading-03";
import Heading4 from "../typography/heading-04";

// helper utils
import {
	selectedUserWithAccessLevel,
	selectedUserWithLoanChannel,
} from "../../utils/common-helpers";
import { BROKER_ADMIN, TPO_SECONDARY } from "../../utils/common-constants";
import { VERBIAGE_CONSTANTS } from "../../utils/verbiage.constants";

// custom modal
import ConfirmationModal from "../modal/confirmation-modal";

// custom styles
import styles from "./index.module.scss";

const drawerWidth = 240;

const CustomLayout = ({ breadcrumb, children, disabledOverflow }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [mobileOpen, setMobileOpen] = useState(false);

	const [confirmModalTitle, setConfirmModalTitle] = useState("");
	const [confirmModalText, setConfirmModalText] = useState("");
	const [confirmModalCloseText, setConfirmModalCloseText] = useState("");
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);

	const handleOpenConfirmModal = () => {
		setConfirmModalVisible(true);
	};

	const handleCloseConfirmModal = () => {
		setConfirmModalVisible(false);
		setConfirmModalTitle("");
		setConfirmModalText("");
		setConfirmModalCloseText("");
	};

	const handleSubmitConfirmModal = () => {
		if (confirmModalTitle === VERBIAGE_CONSTANTS.SIGN_IN_BROKER) {
			navigate("/admin");
		}
		handleCloseConfirmModal();
	};

	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);

	console.log("45 user details:", userDetails);
	console.log("45 impersonated user:", impersonatedUser);

	const isAdmin = userDetails?.user_roles?.includes("admin");
	const isImpersonatedUser =
		impersonatedUser?.email !== undefined &&
		impersonatedUser?.email !== null &&
		impersonatedUser?.email !== "";

	console.log("55 is admin:", isAdmin);
	console.log("55 is impersonated user:", isImpersonatedUser);

	const currentPath = location.pathname;

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const handleDrawerItemClick = (title, url) => {
		handleDrawerToggle();
		console.log("75 title:", title);
		console.log("75 url:", url);

		if (title === "Price my Loan") {
			window.open("https://www.lfclift.com/", "_blank");
		} else if (title === "Order Appraisal") {
			window.open("https://lender.reggora.com/auth/login", "_blank");
		} else if (title === "Schedule Training") {
			window.open(
				"https://appt.link/train-with-logan-finance/submission-portal-training",
				"_blank"
			);
		} else if (title === "Training Videos") {
			window.open("https://loganfinance.com/loganconnect-videos/", "_blank");
		} else {
			if (url === "/new-loan" && isAdmin) {
				if (isImpersonatedUser) {
					navigate(url);
				} else {
					setConfirmModalTitle(VERBIAGE_CONSTANTS.SIGN_IN_BROKER);
					setConfirmModalText("Sign In");
					setConfirmModalCloseText("Close");
					handleOpenConfirmModal();
				}
			} else {
				navigate(url);
			}
		}
	};

	const shouldShow =
		userDetails?.user_roles?.includes("admin") &&
		impersonatedUser?.email &&
		impersonatedUser?.user_tpo_id != null &&
		impersonatedUser?.user_tpo_id !== "";
	const showOrganizationDropdown =
		["/home", "/pipeline", "/upload-history", "/quick-pricer", "/settings"].includes(
			location.pathname
		);
	return (
		<Box className={styles.rootContainer}>
			<CssBaseline />
			<CustomDrawer
				drawerWidth={drawerWidth}
				handleDrawerItemClick={handleDrawerItemClick}
				mobileOpen={mobileOpen}
				handleDrawerToggle={handleDrawerToggle}
			/>

			{/* content right */}
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					p: 0,
					width: { sm: `calc(100% - ${drawerWidth}px)` },
					height: disabledOverflow && "100vh",
				}}
				className={styles.mainContainer}
			>
				<AppBar position="relative" className={styles.appBar}>
					<Toolbar>
						<IconButton
							color="inherit"
							aria-label="open drawer"
							edge="start"
							onClick={handleDrawerToggle}
							sx={{ mr: 2, display: { sm: "none" } }}
							className={styles.menuIcon}
						>
							<MenuIcon />
						</IconButton>

						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							className={styles.toolbarStack}
						>
							{/* breadcrumbs */}
							{breadcrumb?.length > 0 && (
								<Stack direction="row" className={styles.breadcrumbContainer}>
									{breadcrumb?.map((item, index) => (
										<Stack direction="row" alignItems="center" key={index}>
											<Heading3
												key={index}
												text={item.label}
												fontType="semibold"
												// do not click last item
												handleClick={() =>
													breadcrumb?.length !== index + 1 &&
													navigate(item?.path)
												}
												extraClass={
													breadcrumb?.length !== index + 1
														? styles.breadcrumb
														: ""
												}
											/>

											{breadcrumb?.length !== index + 1 && (
												<Box className={styles.breadcrumbIconContainer}>
													<ArrowForwardIosOutlined
														className={styles.breadcrumbIcon}
													/>
												</Box>
											)}
										</Stack>
									))}
								</Stack>
							)}

							<Stack flexDirection="row" alignItems="center" gap={2}>
								{/* user role */}
								<Heading4
									text={
										(userDetails?.user_roles?.includes("manager") ||
										userDetails?.user_roles?.includes("AM")
											? "Account Manager"
											: userDetails?.user_roles?.includes("executive") ||
												  userDetails?.user_roles?.includes("AE")
												? "Account Executive"
												: userDetails?.user_roles?.includes("PA") ||
													  userDetails?.user_roles?.includes("assistant")
													? "Production Assistant"
													: userDetails?.user_roles?.includes("admin")
														? impersonatedUser?.email !== null &&
															impersonatedUser?.email !== undefined &&
															impersonatedUser?.email !== ""
															? `Admin Logged in as ${(impersonatedUser?.email).toLowerCase()}`
															: "Admin"
														: userDetails?.access_level === TPO_SECONDARY
															? TPO_SECONDARY
															: userDetails?.access_level?.includes("admin")
																? // if broker admin
																	userDetails?.email
																: // if broker
																	userDetails?.email) +
										`${
											userDetails?.user_roles?.includes("admin") &&
											impersonatedUser?.email !== null &&
											impersonatedUser?.email !== undefined &&
											impersonatedUser?.email !== "" &&
											selectedUserWithAccessLevel(userDetails, impersonatedUser)
												?.access_level === TPO_SECONDARY
												? TPO_SECONDARY
												: ""
										}`
									}
									extraClass={styles.userRoleText}
								/>

								{!shouldShow && showOrganizationDropdown && (
									<OrganizationDropdown />
								)}
							</Stack>
						</Stack>
					</Toolbar>
				</AppBar>

				{children}
			</Box>

			{/* modal */}
			<ConfirmationModal
				open={confirmModalVisible}
				text={confirmModalTitle}
				confirmText={confirmModalText}
				handleConfirm={handleSubmitConfirmModal}
				closeText={confirmModalCloseText}
				handleClose={handleCloseConfirmModal}
			/>
		</Box>
	);
};

export default CustomLayout;
